import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { PageCard } from '../common/PageCard';
import { PageToolbar } from '../common/PageToolbar';
import { UniversalPagination } from '../common/UniversalPagination';
import { toastService } from '../../services/toast.service';
import { useClientOptions } from '../../hooks/useClientOptions';
import { 
  getExpertBulkNotifications, 
  sendExpertBulkNotification 
} from '../../services/notification.service';

// New Components
import { 
  BulkNotificationModal, 
  BulkNotificationData 
} from './bulk-notifications/BulkNotificationModal';
import { 
  BulkNotificationsTable, 
  BulkNotificationItem 
} from './bulk-notifications/BulkNotificationsTable';
import { BulkNotificationDetailsModal } from './bulk-notifications/BulkNotificationDetailsModal';

export const ClientsBulkNotificationsTab: React.FC = () => {
  const { groups } = useClientOptions();
  const [notifications, setNotifications] = useState<BulkNotificationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailsModalItem, setDetailsModalItem] = useState<BulkNotificationItem | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getExpertBulkNotifications({ page, limit });
      const items: BulkNotificationItem[] = res.data.map(item => ({
        id: item.id,
        createdAt: item.createdAt,
        message: item.message,
        level: item.level,
        targetType: item.targetType,
        targetGroupId: item.targetGroupId,
        groupNames: item.targetGroupName ? [item.targetGroupName] : undefined,
        sentCount: item.sentCount,
      }));
      setNotifications(items);
      setTotal(res.meta.total);
    } catch (error) {
      toastService.error('Не вдалося завантажити історію розсилок.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSendNotification = async (data: BulkNotificationData) => {
    try {
      await sendExpertBulkNotification({
        message: data.message,
        level: data.level,
        targetType: data.targetType,
        groupId: data.groupId || undefined,
      });
      
      toastService.success('Масове сповіщення успішно надіслано.');
      setPage(1);
      await fetchHistory();
    } catch (error) {
      toastService.error('Не вдалося надіслати масове сповіщення.');
      throw error;
    }
  };

  return (
    <Box data-i-doc={TabIndices.CLIENTS_BULK_NOTIFICATIONS}>

      <BulkNotificationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSend={handleSendNotification}
        groups={groups}
      />

      <BulkNotificationDetailsModal
        open={!!detailsModalItem}
        onClose={() => setDetailsModalItem(null)}
        notification={detailsModalItem}
      />

      <PageToolbar
        storageKey="filters:bulk-notifications"
        filters={[]}
        extraActions={
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setIsModalOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Створити розсилку
          </Button>
        }
      />

      <PageCard>
        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <BulkNotificationsTable 
            notifications={notifications} 
            onViewDetails={(item) => setDetailsModalItem(item)}
          />
        )}
        
        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          total={total}
          limit={limit}
          onPageChange={setPage}
          storageKey="pagination:bulk-notifications"
        />
      </PageCard>
    </Box>
  );
};
